import { useState } from 'react';
import { LuCalendar } from 'react-icons/lu';

import { controlBase, controlStates } from '@mds/shared/lib/control';
import { minutesOf, timeValue } from '@mds/shared/lib/dates';
import { clockLabel, clockRange, dateKeyOf, longDateLabel, parseDateKey } from '@mds/shared/lib/format';
import { FREE_TRIAL_STEP_MINUTES } from '@mds/shared/lib/free-trial';
import { cn } from '@mds/shared/lib/utils';
import Select, { type SelectOption } from '@mds/shared/ui/select';
import { Calendar } from '@mds/shared/ui/shadcn/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@mds/shared/ui/shadcn/popover';

/** The first and last value a picker can take: `yyyy-mm-dd` or `hh:mm`. */
type Range = { min?: string; max?: string };

/**
 * Every trial-step time in `range`, valued `hh:mm` as the form holds it and
 * labelled "8:00 AM", so AM and PM are always spelled out rather than left to
 * the browser's locale the way a native time input leaves them.
 */
function timeOptions({ min = '00:00', max = '23:30' }: Range): SelectOption[] {
  const options: SelectOption[] = [];
  for (let m = minutesOf(min); m <= minutesOf(max); m += FREE_TRIAL_STEP_MINUTES) {
    options.push({ value: timeValue(m), label: clockLabel(m) });
  }
  return options;
}

/**
 * A day and a time on it, in one box: "19/10/26 · 9:00 AM – 12:00 PM". Pressed, it
 * opens the calendar with the two times under it, and closes on its own once
 * all three are set and the end is after the start. A press outside closes it
 * too, as with any popover.
 *
 * One box rather than three, so a row of them fits a phone beside the floor
 * picker. Drawn in the same box as DatePicker, so it reads as the same kind of
 * thing. Each value is its own, as the form holds them: the box only gathers
 * them.
 *
 * Until both times are in, the box says what is missing in the muted
 * placeholder tone, so a half-filled one does not read as done.
 */
export default function WhenPicker({
  date,
  from,
  to,
  onDateChange,
  onFromChange,
  onToChange,
  onClose,
  dates = {},
  fromTimes = {},
  toTimes = {},
  className,
  'aria-invalid': ariaInvalid,
}: {
  date: string;
  from: string;
  to: string;
  onDateChange: (next: string) => void;
  onFromChange: (next: string) => void;
  onToChange: (next: string) => void;
  /** When the box is left: the form's blur, so its messages show then. */
  onClose?: () => void;
  dates?: Range;
  fromTimes?: Range;
  toTimes?: Range;
  className?: string;
  'aria-invalid'?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = date ? parseDateKey(date) : undefined;
  const unpickable = [
    ...(dates.min ? [{ before: parseDateKey(dates.min) }] : []),
    ...(dates.max ? [{ after: parseDateKey(dates.max) }] : []),
  ];
  const complete = Boolean(date && from && to);
  const label = !date
    ? 'Pick a date and time'
    : from && to
      ? `${longDateLabel(date)} · ${clockRange(minutesOf(from), minutesOf(to))}`
      : `${longDateLabel(date)} · Pick a time`;

  // The end list offers only times after the start, so a backwards range
  // cannot be picked: the schema's own check stays as the backstop.
  const fromOptions = timeOptions(fromTimes);
  const toOptions = timeOptions(toTimes).filter((option) => !from || option.value > from);

  function changeOpen(next: boolean) {
    setOpen(next);
    if (!next) onClose?.();
  }

  /**
   * Closes the box if all three are set and in order: there is no Done to
   * press, the last pick is the end. Takes the values to check, since right
   * after a change the props still hold the old ones.
   */
  function closeIfComplete(values: { date: string; from: string; to: string }) {
    if (values.date && values.from && values.to && values.to > values.from) changeOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-invalid={ariaInvalid}
          className={cn(
            controlBase,
            controlStates,
            'h-9 items-center justify-between gap-2 text-left',
            !complete && 'text-ink-muted',
            className,
          )}
        >
          <span className="truncate">{label}</span>
          <LuCalendar aria-hidden="true" className="size-4 shrink-0 text-ink-muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        {/* Picking a day leaves the box open while a time is still missing. */}
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          disabled={unpickable}
          onSelect={(day) => {
            if (!day) return;
            onDateChange(dateKeyOf(day));
            closeIfComplete({ date: dateKeyOf(day), from, to });
          }}
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3 border-t border-hairline p-3">
          <div className="flex flex-col gap-1.5">
            <span className="font-mono text-2xs text-ink-muted uppercase">From</span>
            <Select
              variant="field"
              className="text-sm"
              aria-label="From"
              placeholder="Start"
              options={fromOptions}
              value={from}
              onChange={(next) => {
                onFromChange(next);
                // A start moved past the end drops the end, rather than
                // leaving a range that runs backwards.
                const nextTo = to && to <= next ? '' : to;
                if (nextTo !== to) onToChange(nextTo);
                closeIfComplete({ date, from: next, to: nextTo });
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="font-mono text-2xs text-ink-muted uppercase">To</span>
            <Select
              variant="field"
              className="text-sm"
              aria-label="To"
              placeholder="End"
              options={toOptions}
              value={to}
              onChange={(next) => {
                onToChange(next);
                closeIfComplete({ date, from, to: next });
              }}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
